import { baseApi, PaginatedResponse } from 'sama-shared';
import type {
  PartnerCollectSettlement,
  PartnerCollectSettlementDetail,
  PartnerCollectDebtSummary,
} from '../types/admin';
import type { GetPartnerCollectSettlementsParams } from '../types/api';

export interface UpdatePartnerCollectSettlementStatusDto {
  status: number;
}

export const partnerCollectSettlementApi = baseApi
  .enhanceEndpoints({ addTagTypes: ['PartnerCollectSettlement'] })
  .injectEndpoints({
    endpoints: (builder) => ({
      getPartnerCollectSettlements: builder.query<
        PaginatedResponse<PartnerCollectSettlement>,
        GetPartnerCollectSettlementsParams
      >({
        query: (params) => ({
          url: '/admin/partner-settlement',
          method: 'GET',
          params,
        }),
        providesTags: (result) =>
          result?.data
            ? [
                ...result.data.map(({ id }) => ({
                  type: 'PartnerCollectSettlement' as const,
                  id,
                })),
                { type: 'PartnerCollectSettlement', id: 'LIST' },
              ]
            : [{ type: 'PartnerCollectSettlement', id: 'LIST' }],
      }),

      getPartnerCollectSettlementById: builder.query<PartnerCollectSettlementDetail, string>({
        query: (id) => ({
          url: `/admin/partner-settlement/${id}`,
          method: 'GET',
        }),
        providesTags: (_result, _error, id) => [{ type: 'PartnerCollectSettlement', id }],
      }),

      getPartnerCollectDebt: builder.query<PartnerCollectDebtSummary, string>({
        query: (partnerId) => ({
          url: `/admin/partner-settlement/debt/${partnerId}`,
          method: 'GET',
        }),
        providesTags: (_result, _error, partnerId) => [
          { type: 'PartnerCollectSettlement', id: `DEBT-${partnerId}` },
        ],
      }),

      getPartnerCollectPendingSettlements: builder.query<
        PartnerCollectSettlement[],
        { partnerId: string }
      >({
        query: ({ partnerId }) => ({
          url: '/admin/partner-settlement/pending/all',
          method: 'GET',
          params: { partnerId },
        }),
        providesTags: (_result, _error, { partnerId }) => [
          { type: 'PartnerCollectSettlement', id: `PENDING-${partnerId}` },
          { type: 'PartnerCollectSettlement', id: 'LIST' },
        ],
      }),

      updatePartnerCollectSettlementStatus: builder.mutation<
        PartnerCollectSettlement,
        { settlementId: string; body: UpdatePartnerCollectSettlementStatusDto }
      >({
        query: ({ settlementId, body }) => ({
          url: `/admin/partner-settlement/${settlementId}`,
          method: 'PATCH',
          body,
        }),
        invalidatesTags: (_result, _error, { settlementId }) => [
          { type: 'PartnerCollectSettlement', id: settlementId },
          { type: 'PartnerCollectSettlement', id: 'LIST' },
          'AdminOrder',
        ],
      }),
    }),
    overrideExisting: false,
  });

export const {
  useGetPartnerCollectSettlementsQuery,
  useGetPartnerCollectSettlementByIdQuery,
  useGetPartnerCollectDebtQuery,
  useLazyGetPartnerCollectDebtQuery,
  useGetPartnerCollectPendingSettlementsQuery,
  useLazyGetPartnerCollectPendingSettlementsQuery,
  useUpdatePartnerCollectSettlementStatusMutation,
} = partnerCollectSettlementApi;
